import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import EditIcon from "@material-ui/icons/Edit";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPut, AuthRequest } from "../../lib";

/**
 * Sales — a port of the legacy sale.php screen.
 *
 * A sale is a site-wide campaign: a discount plus the banner copy and artwork
 * the storefront shows while it runs. It shares order_coupon with the Coupons
 * screen, separated by is_sale.
 *
 * Laid out as the legacy form is, which matters for how much room it takes:
 * the discount, model numbers and flags sit in a block of 8 columns with the
 * brand picker BESIDE them in the remaining 4, not stacked underneath. The
 * whole form stays about half a screen so the list of sales is visible under
 * it without scrolling.
 */

const STATE_COLOR = {
  Running: "27,94,32",
  Scheduled: "13,71,161",
  Ended: "183,28,28",
  Inactive: "224,168,0",
};

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1180, margin: "24px auto 0", width: "100%" },
  panel: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  title: { fontSize: 18, fontWeight: 500, flex: "none" },
  formTitle: {
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: ".02em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.25),
  },
  spacer: { flex: 1 },
  search: {
    width: 240,
    "& .MuiOutlinedInput-root": { height: 31 },
    "& .MuiOutlinedInput-input": { padding: "0 4px", fontSize: 13 },
    "& .MuiInputAdornment-root": { marginRight: 2 },
  },
  searchIcon: { fontSize: 17, color: theme.palette.text.secondary },
  count: { color: theme.palette.text.secondary, fontSize: 13 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: theme.spacing(1.25),
    alignItems: "start",
  },
  /* The 8/4 split: everything you set on the left, brands on the right. */
  main: { gridColumn: "span 8", display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: theme.spacing(1.25), alignContent: "start" },
  side: { gridColumn: "span 4" },
  flags: {
    gridColumn: "span 8",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: -4,
  },
  copyCol: {
    gridColumn: "span 6",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
  },
  chips: {
    gridColumn: "span 8",
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    minHeight: 22,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "1px 5px 1px 7px",
    borderRadius: 3,
    fontSize: 11.5,
    background: "rgba(49,81,121,0.10)",
    color: "#315179",
  },
  chipPre: { background: "rgba(74,20,140,0.10)", color: "#4a148c" },
  chipX: { cursor: "pointer", fontWeight: 700, opacity: 0.55, "&:hover": { opacity: 1 } },
  /* Two file pickers side by side read as four loose controls unless each is
     boxed with its own caption — otherwise it is not obvious which Choose
     belongs to which image. */
  fileBox: {
    gridColumn: "span 4",
    border: "1px solid rgba(0,0,0,0.16)",
    borderRadius: 3,
    padding: theme.spacing(0, 1),
    // Matches an outlined field, so this row lines up along the bottom.
    height: 40,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  fileLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: ".04em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  fileBody: { display: "flex", alignItems: "center", gap: theme.spacing(1), flex: 1, minWidth: 0 },
  fileName: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileNone: { fontStyle: "italic", opacity: 0.7 },
  thumb: {
    width: 26,
    height: 26,
    objectFit: "contain",
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 3,
    background: "#fff",
  },
  smallBtn: { fontSize: 11, minWidth: 0, padding: "2px 8px" },
  row: { height: 36 },
  cell: { fontSize: 13, padding: "4px 10px" },
  headCell: { fontSize: 12, fontWeight: 600, padding: "6px 10px" },
  num: { fontVariantNumeric: "tabular-nums", textAlign: "right" },
  muted: { color: theme.palette.text.secondary },
  strong: { color: "rgba(0,0,0,0.87)" },
  truncate: { maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  state: {
    display: "inline-block",
    padding: "1px 7px",
    borderRadius: 3,
    fontSize: 11,
    fontWeight: 700,
  },
  actions: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
  editBtn: { padding: 4, color: "rgba(0,0,0,0.4)", "&:hover": { color: "#315179" } },
  linkBtn: {
    fontSize: 12,
    padding: 0,
    minWidth: 0,
    textTransform: "none",
    color: theme.palette.primary.main,
  },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  ok: { color: "#1b5e20", fontSize: 13, marginTop: theme.spacing(1) },
  loading: { padding: theme.spacing(3), textAlign: "center" },
}));

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

const when = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(s);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

/** A DATETIME back into what a datetime-local input wants. */
const toLocalInput = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

/**
 * sale.js makeSaleId(5): five letters, no digits and no O — a different
 * alphabet from the coupon screen's generator, which does use digits.
 */
function generateSaleCode() {
  const alphabet = "ABCDEFGHIJKLMNPQRSTUVWXYZ";
  let out = "";
  for (let i = 0; i < 5; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

const EMPTY = {
  coupon_code: "",
  coupon_name: "",
  start: "",
  end: "",
  percent: "",
  value: "",
  mens: false,
  womens: false,
  only_on_special: false,
  active: true,
  brands: [],
  header_text: "",
  mobile_header_text: "",
  item_view_text: "",
  sale_notes: "",
  popup_link: "",
};

export function SalesPage() {
  const classes = useStyles();

  const [rows, setRows] = useState([]);
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [model, setModel] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [existingFiles, setExistingFiles] = useState({ stamp: null, popup: null });
  const [stampFile, setStampFile] = useState(null);
  const [popupFile, setPopupFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const q = query ? `?search=${encodeURIComponent(query)}` : "";
    const { data, error: err } = await authGet(`/sales${q}`);
    setLoading(false);
    if (err) {
      setError(err.message || "Could not load sales.");
      return;
    }
    setRows(data?.rows ?? []);
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    authGet("/sales/brands").then(({ data }) => setBrands(data?.rows ?? []));
  }, []);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));
  const setFlag = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.checked }));

  const reset = () => {
    setForm(EMPTY);
    setItems([]);
    setModel("");
    setEditingId(null);
    setExistingFiles({ stamp: null, popup: null });
    setStampFile(null);
    setPopupFile(null);
    setError("");
  };

  /** Same lookup the coupon screen uses; P + stock number for pre-owned. */
  const addItem = async () => {
    const wanted = model.trim();
    if (!wanted) {
      setError("Please enter a model number.");
      return;
    }
    setError("");

    const { data, error: err } = await authGet(
      `/orders/item-lookup?model_no=${encodeURIComponent(wanted)}`,
    );
    if (err) {
      setError(err.message || "Could not look that model up.");
      return;
    }
    if (!data?.found) {
      setError(`Model number not found: ${wanted}`);
      return;
    }

    // Stored as the ITEM id, P-prefixed for pre-owned.
    const token = data.is_preowned ? `P${data.item_id}` : String(data.item_id);
    if (!items.some((i) => i.token === token)) {
      setItems((l) => [
        ...l,
        { token, label: data.label || data.model_no, preowned: !!data.is_preowned },
      ]);
    }
    setModel("");
  };

  const edit = async (r) => {
    setEditingId(r.coupon_id);
    setError("");
    setNotice("");
    setStampFile(null);
    setPopupFile(null);
    setExistingFiles({ stamp: r.stamp_file_name, popup: r.popup_file_name });
    setForm({
      coupon_code: r.coupon_code ?? "",
      coupon_name: r.coupon_name ?? "",
      start: toLocalInput(r.start),
      end: toLocalInput(r.end),
      percent: Number(r.percent) ? "1" : "0",
      // Shown as typed: the column holds 0.03 for 3%.
      value: String(r.displayValue ?? ""),
      mens: !!r.mens,
      womens: !!r.womens,
      only_on_special: !!Number(r.only_on_special),
      active: !!Number(r.active),
      brands: r.brandIds ?? [],
      header_text: r.header_text ?? "",
      mobile_header_text: r.mobile_header_text ?? "",
      item_view_text: r.item_view_text ?? "",
      sale_notes: r.sale_notes ?? "",
      popup_link: r.popup_link ?? "",
    });

    // Resolved through the coupon endpoint — the column has the same shape.
    const tokens = String(r.required_items || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    setItems(tokens.map((t) => ({ token: t, label: t, preowned: /^P/i.test(t) })));
    if (tokens.length) {
      const { data } = await authGet(`/coupons/${r.coupon_id}/items`);
      if (data?.items) {
        setItems(
          data.items.map((i) => ({
            token: i.token,
            label: i.label || i.token,
            preowned: !!i.preowned,
          })),
        );
      }
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (form.percent === "") {
      setError("Choose a discount type.");
      return;
    }
    // sale.js refuses a fractional percent before the form ever posts.
    if (form.percent === "1" && form.value !== "" && !Number.isInteger(Number(form.value))) {
      setError("Percent must be a whole number.");
      return;
    }

    setBusy(true);
    setError("");
    setNotice("");

    // Two file inputs, so the form posts as multipart rather than JSON.
    const body = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === "brands") body.append("brands", (v ?? []).join(","));
      else if (typeof v === "boolean") body.append(k, v ? "1" : "0");
      else body.append(k, v ?? "");
    });
    body.append("required_items", items.map((i) => i.token).join(","));
    if (stampFile) body.append("stamp_file", stampFile);
    if (popupFile) body.append("popup_file", popupFile);

    try {
      if (editingId) await AuthRequest.put(`/sales/${editingId}`, body);
      else await AuthRequest.post("/sales", body);
      setNotice(editingId ? "Sale updated." : "Sale created.");
      reset();
      load();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Could not save that sale.");
    }
    setBusy(false);
  };

  const toggle = (r) => async () => {
    const { error: err } = await authPut(`/sales/${r.coupon_id}/status`, {});
    if (err) {
      setError(err.message || "Could not change that sale.");
      return;
    }
    load();
  };

  const submitSearch = (e) => {
    e?.preventDefault?.();
    setQuery(search.trim());
  };

  const percentMode = form.percent === "1";

  return (
    <Box className={classes.page}>
      <Paper className={classes.panel}>
        <div className={classes.formTitle}>
          {editingId ? `Edit Sale #${editingId}` : "New Sale"}
        </div>

        <div className={classes.grid}>
          <TextField
            style={{ gridColumn: "span 3" }}
            label="Code"
            placeholder="Enter or generate a code"
            value={form.coupon_code}
            onChange={set("coupon_code")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    className={classes.linkBtn}
                    onClick={() => setForm((f) => ({ ...f, coupon_code: generateSaleCode() }))}
                  >
                    Generate
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            style={{ gridColumn: "span 3" }}
            label="Name"
            value={form.coupon_name}
            onChange={set("coupon_name")}
          />
          <TextField
            style={{ gridColumn: "span 3" }}
            label="Starts"
            type="datetime-local"
            value={form.start}
            onChange={set("start")}
          />
          <TextField
            style={{ gridColumn: "span 3" }}
            label="Ends"
            type="datetime-local"
            value={form.end}
            onChange={set("end")}
          />

          {/* Left block: discount, model numbers, flags. */}
          <div className={classes.main}>
            <TextField
              select
              style={{ gridColumn: "span 2" }}
              label="Discount Type"
              value={form.percent}
              // Clearing the type clears the amount: $10 and 10% are not the
              // same number, and sale.js wipes it for exactly that reason.
              onChange={(e) => setForm((f) => ({ ...f, percent: e.target.value, value: "" }))}
            >
              <MenuItem value="">
                <em>-- Select --</em>
              </MenuItem>
              <MenuItem value="0">Dollar $</MenuItem>
              <MenuItem value="1">Percent %</MenuItem>
            </TextField>
            <TextField
              style={{ gridColumn: "span 2" }}
              label={percentMode ? "Discount (% off)" : "Discount ($ off)"}
              type="number"
              value={form.value}
              onChange={set("value")}
              disabled={form.percent === ""}
            />
            <TextField
              style={{ gridColumn: "span 4" }}
              label="Model Number"
              placeholder="Model number, or P + stock number"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addItem();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button size="small" className={classes.linkBtn} onClick={addItem}>
                      Add
                    </Button>
                  </InputAdornment>
                ),
              }}
            />

            <div className={classes.flags}>
              <FormControlLabel
                control={<Checkbox color="primary" size="small" checked={form.mens} onChange={setFlag("mens")} />}
                label="Men's"
              />
              <FormControlLabel
                control={<Checkbox color="primary" size="small" checked={form.womens} onChange={setFlag("womens")} />}
                label="Women's"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={form.only_on_special}
                    onChange={setFlag("only_on_special")}
                  />
                }
                label="On Special"
              />
            </div>

            <div className={classes.chips}>
              {items.map((i) => (
                <span
                  key={i.token}
                  className={`${classes.chip} ${i.preowned ? classes.chipPre : ""}`}
                  title={i.token}
                >
                  {i.label}
                  <span
                    className={classes.chipX}
                    onClick={() => {
                      // eslint-disable-next-line no-alert
                      if (!window.confirm("Are you sure you want to remove this item?")) return;
                      setItems((l) => l.filter((x) => x.token !== i.token));
                    }}
                  >
                    ×
                  </span>
                </span>
              ))}
            </div>
          </div>

          {/* Right block: brands, beside the discount rather than below it.
              Type to narrow 31 brands rather than hunting a long dropdown;
              each pick becomes a chip you can remove. */}
          <div className={classes.side}>
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              options={brands}
              value={brands.filter((b) => form.brands.includes(String(b.brand_id)))}
              getOptionLabel={(o) => o.brand || ""}
              getOptionSelected={(o, v) => String(o.brand_id) === String(v.brand_id)}
              onChange={(_, picked) =>
                setForm((f) => ({ ...f, brands: picked.map((b) => String(b.brand_id)) }))
              }
              renderTags={(value, getTagProps) =>
                value.map((b, i) => (
                  <Chip
                    {...getTagProps({ index: i })}
                    key={b.brand_id}
                    size="small"
                    label={b.brand}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Brands"
                  placeholder={form.brands.length ? "" : "All brands — type to narrow"}
                  helperText={
                    form.brands.length
                      ? `${form.brands.length} selected`
                      : "Leave empty to include every brand"
                  }
                />
              )}
            />
          </div>

          {/* Two stacked columns rather than one row of four: the header pair
              belongs together on the left, the item/notes pair on the right. */}
          <div className={classes.copyCol}>
            <TextField
              label="Header Text"
              multiline
              rows={3}
              value={form.header_text}
              onChange={set("header_text")}
            />
            <TextField
              label="Mobile Header Text"
              multiline
              rows={3}
              value={form.mobile_header_text}
              onChange={set("mobile_header_text")}
            />
          </div>
          <div className={classes.copyCol}>
            <TextField
              label="Item View Text"
              multiline
              rows={3}
              value={form.item_view_text}
              onChange={set("item_view_text")}
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={form.sale_notes}
              onChange={set("sale_notes")}
            />
          </div>

          <div className={classes.fileBox}>
            <div className={classes.fileLabel}>Stamp Image</div>
            <div className={classes.fileBody}>
              {existingFiles.stamp && !stampFile && (
                <img
                  className={classes.thumb}
                  src={`/sale-images/${existingFiles.stamp}`}
                  alt="Current stamp"
                />
              )}
              <span
                className={`${classes.fileName} ${
                  stampFile || existingFiles.stamp ? "" : classes.fileNone
                }`}
                title={stampFile?.name || existingFiles.stamp || ""}
              >
                {stampFile ? stampFile.name : existingFiles.stamp || "No file chosen"}
              </span>
              <Button size="small" variant="outlined" component="label" className={classes.smallBtn}>
                Choose
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => setStampFile(e.target.files?.[0] ?? null)}
                />
              </Button>
            </div>
          </div>

          <div className={classes.fileBox}>
            <div className={classes.fileLabel}>Popup Image</div>
            <div className={classes.fileBody}>
              {existingFiles.popup && !popupFile && (
                <img
                  className={classes.thumb}
                  src={`/sale-images/${existingFiles.popup}`}
                  alt="Current popup"
                />
              )}
              <span
                className={`${classes.fileName} ${
                  popupFile || existingFiles.popup ? "" : classes.fileNone
                }`}
                title={popupFile?.name || existingFiles.popup || ""}
              >
                {popupFile ? popupFile.name : existingFiles.popup || "No file chosen"}
              </span>
              <Button size="small" variant="outlined" component="label" className={classes.smallBtn}>
                Choose
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => setPopupFile(e.target.files?.[0] ?? null)}
                />
              </Button>
            </div>
          </div>

          <TextField
            style={{ gridColumn: "span 3" }}
            label="Links To"
            placeholder="Stub"
            value={form.popup_link}
            onChange={set("popup_link")}
          />

          {/* Active sits here in the legacy form, beside Links To, rather than
              with the targeting checkboxes further up. */}
          <div style={{ gridColumn: "span 1", paddingTop: 6 }}>
            <FormControlLabel
              control={
                <Checkbox color="primary" size="small" checked={form.active} onChange={setFlag("active")} />
              }
              label="Active"
            />
          </div>

          <div style={{ gridColumn: "span 12", display: "flex", gap: 8 }}>
            <Button size="small" variant="contained" color="primary" disabled={busy} onClick={save}>
              {busy ? "Saving…" : editingId ? "Save changes" : "Save Sale"}
            </Button>
            {editingId && (
              <Button size="small" onClick={reset} disabled={busy}>
                Cancel
              </Button>
            )}
          </div>
        </div>

        {error && <div className={classes.error}>{error}</div>}
        {notice && <div className={classes.ok}>{notice}</div>}
      </Paper>

      <Paper className={classes.panel}>
        <div className={classes.head}>
          <Typography className={classes.title}>Sales</Typography>

          <form onSubmit={submitSearch}>
            <TextField
              className={classes.search}
              placeholder="Code or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={submitSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </form>

          <div className={classes.spacer} />
          <span className={classes.count}>
            {loading ? "Loading…" : `${rows.length.toLocaleString()} shown`}
          </span>
        </div>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell} style={{ width: 110 }}>Code</TableCell>
              <TableCell className={classes.headCell} style={{ width: 150 }}>Start Time</TableCell>
              <TableCell className={classes.headCell} style={{ width: 150 }}>End Time</TableCell>
              <TableCell className={classes.headCell}>Name</TableCell>
              <TableCell className={`${classes.headCell} ${classes.num}`} style={{ width: 90 }}>
                Value
              </TableCell>
              <TableCell className={`${classes.headCell} ${classes.num}`} style={{ width: 80 }}>
                # Used
              </TableCell>
              <TableCell className={classes.headCell} style={{ width: 100 }}>Status</TableCell>
              <TableCell className={classes.headCell} style={{ width: 140 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const rgb = STATE_COLOR[r.state] ?? "97,97,97";
              return (
                <TableRow key={r.coupon_id} className={classes.row} hover>
                  <TableCell className={`${classes.cell} ${classes.strong}`}>
                    {r.coupon_code}
                  </TableCell>
                  <TableCell className={classes.cell}>{when(r.start)}</TableCell>
                  <TableCell className={classes.cell}>{when(r.end)}</TableCell>
                  <TableCell className={`${classes.cell} ${classes.truncate}`} title={r.coupon_name}>
                    {r.coupon_name}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.num}`}>
                    {Number(r.percent)
                      ? `${r.displayValue}%`
                      : Number(r.value) > 0
                      ? money(r.value)
                      : money(r.order_total)}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.num}`}>
                    {r.count_applied}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <span
                      className={classes.state}
                      style={{ background: `rgba(${rgb},0.16)`, color: `rgb(${rgb})` }}
                    >
                      {r.state}
                    </span>
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.actions}`}>
                    <Button className={classes.linkBtn} onClick={toggle(r)}>
                      {Number(r.active) ? "Deactivate" : "Activate"}
                    </Button>
                    <IconButton
                      size="small"
                      className={classes.editBtn}
                      title="Edit this sale"
                      onClick={() => edit(r)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {loading && (
          <div className={classes.loading}>
            <CircularProgress size={22} />
          </div>
        )}
        {!loading && !rows.length && (
          <div className={classes.loading}>
            <span className={classes.muted}>No sales match.</span>
          </div>
        )}
      </Paper>
    </Box>
  );
}

export default SalesPage;
